import React from "react";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Paper, Typography } from "@mui/material";
import { AttachFile } from "@mui/icons-material";

export interface ParticipantDetails {
  participantId: string;
  studyCode: string;
  status: string;
  enrolledAt?: string;
  authorizedAt?: string;
  additionalInfo?: string;
  documents?: { name: string; type: string; size: number; uploadedAt: string }[];
  informationCompleted?: boolean;
  informationCompletedAt?: string;
}

interface Props {
  open: boolean;
  participant: ParticipantDetails | null;
  onClose: () => void;
}

export default function ParticipantDetailsDialog({ open, participant, onClose }: Props) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Participant Details</DialogTitle>
      <DialogContent>
        {participant && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Basic Information */}
            <Paper sx={{ p: 3, bgcolor: "primary.50" }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}
              >
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Participant ID:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, fontFamily: "monospace" }}
                  >
                    {participant.participantId}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Study Information */}
            <Paper sx={{ p: 3, bgcolor: "info.50" }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 2, color: "info.main" }}
              >
                Study Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Study Code:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, fontFamily: "monospace" }}
                  >
                    {participant.studyCode}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status:
                  </Typography>
                  <Chip
                    label={participant.status}
                    color={
                      participant.status === "enrolled"
                        ? "success"
                        : "default"
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Enrolled Date:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {participant.enrolledAt
                      ? new Date(
                          participant.enrolledAt,
                        ).toLocaleDateString()
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Authorized Date:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {participant.authorizedAt
                      ? new Date(
                          participant.authorizedAt,
                        ).toLocaleDateString()
                      : "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Additional Information */}
            {participant.additionalInfo && (
              <Paper sx={{ p: 3, bgcolor: "success.50" }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, mb: 2, color: "success.main" }}
                >
                  Additional Information
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
                >
                  {participant.additionalInfo}
                </Typography>
              </Paper>
            )}

            {/* Documents */}
            {participant.documents &&
              participant.documents.length > 0 && (
                <Paper sx={{ p: 3, bgcolor: "warning.50" }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, color: "warning.main" }}
                  >
                    Uploaded Documents (
                    {participant.documents.length})
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {participant.documents.map(
                      (doc, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            p: 1,
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <AttachFile sx={{ color: "text.secondary" }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {doc.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {doc.type} • {Math.round(doc.size / 1024)} KB
                              •{" "}
                              {new Date(
                                doc.uploadedAt,
                              ).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                      ),
                    )}
                  </Box>
                </Paper>
              )}

            {/* Information Completion Status */}
            <Paper
              sx={{
                p: 3,
                bgcolor: participant.informationCompleted
                  ? "success.50"
                  : "error.50",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: participant.informationCompleted
                    ? "success.main"
                    : "error.main",
                }}
              >
                Information Status
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label={
                    participant.informationCompleted
                      ? "Completed"
                      : "Incomplete"
                  }
                  color={
                    participant.informationCompleted
                      ? "success"
                      : "error"
                  }
                  size="small"
                />
                {participant.informationCompletedAt && (
                  <Typography variant="body2" color="text.secondary">
                    Completed on{" "}
                    {new Date(
                      participant.informationCompletedAt,
                    ).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
